<template>
  <div
    v-show="showRow"
    class="row"
    :class="{ isActiveRow: isSelected }"
    @mouseenter="showButtons = true"
    @mouseleave="showButtons = false"
    @click="searchFilterStore.setCurrentSearchFilter(savedFilter)"
  >
    <span
      :class="
        isSelected
          ? 'font-caption-1-strong isActiveRowText'
          : 'font-caption-1-medium'
      "
      >{{ savedFilter.name }}</span
    >
    <div v-if="showButtons" class="flex gap-x-2">
      <PrimaryButton
        variant="transparent"
        :custom-class="' border-none'"
        @on-click="deleteSearchFilter"
      >
        <ClientOnly>
          <font-awesome-icon
            class="text-danger-600"
            :icon="['fas', 'trash-can']"
          />
        </ClientOnly>
      </PrimaryButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Ref, ref, computed } from "vue";
import { ElNotification } from "element-plus";
import { useI18n } from "vue-i18n";
import { useSearchFiltersStore } from "~/store/searchFilter";
import PrimaryButton from "~/components/shared/primary-button/PrimaryButton.vue";

const { t } = useI18n();
const searchFilterStore = useSearchFiltersStore();

const showRow: Ref<boolean> = ref(true);
const showButtons: Ref<Boolean> = ref(false);

const props = defineProps({
  savedFilter: {
    type: Object,
    required: true,
  },
});

const toggleShowRow = () => {
  showRow.value = !showRow.value;
};

let undoClicked = false;

const deleteSearchFilter = async () => {
  toggleShowRow();

  let success = false;
  // Track if the filter was initially deleted
  let filterDeleted = false;

  try {
    success = await searchFilterStore.deleteSearchFilter(props.savedFilter.id);
    // Set to true if delete was successful
    filterDeleted = success;
    if (isSelected.value) {
      // Reset to default filter
      searchFilterStore.setCurrentSearchFilter(null);
    }
  } catch (error) {
    /* eslint-disable no-console */
    console.error("Error deleting search filter:", error);
    // Revert the row if deletion failed
    toggleShowRow();
    return;
  }

  const notification = ElNotification({
    customClass: "notification-xs info",
    dangerouslyUseHTMLString: true,
    message: `
      <div class="flex items-center gap-2">
        <div class="w-4 h-4 flex items-center justify-center">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path fill="#2563EB" d="M7.33 11.33h1.33V7.33H7.33v4ZM8 6a.67.67 0 1 0 0-1.33A.67.67 0 0 0 8 6Zm0 8.67a6.67 6.67 0 1 1 0-13.33 6.67 6.67 0 0 1 0 13.33ZM8 13.33a5.33 5.33 0 1 0 0-10.67 5.33 5.33 0 0 0 0 10.67Z"/>
          </svg>
        </div>
        <span class="flex-1 text-slate-900 font-sans text-xs not-italic font-normal">${t("appliedFilters.filterDeleted")}</span>
        <button id="deleteSearchFilterButton-${props.savedFilter.id}" class="text-slate-900 font-sans text-xs not-italic font-semibold">${t("common.undo")}</button>
      </div>
    `,
    position: "bottom-left",
    duration: 5000,
    offset: 50,
    onClose: () => {
      if (undoClicked) {
        undoClicked = false;
      }
    },
  });

  // Add a callback to the 'UNDO' button only if the filter was initially deleted
  if (filterDeleted) {
    const button = document.getElementById(
      `deleteSearchFilterButton-${props.savedFilter.id}`,
    );
    if (button) {
      button.addEventListener("click", () => {
        undoClicked = true;
        searchFilterStore.revertDeleteSearchFilter(props.savedFilter.id);
        notification.close();
      });
    }
  }
};

const isSelected = computed(() => {
  return searchFilterStore.currentSearchFilter?.id === props.savedFilter?.id;
});
</script>

<style scoped>
.row {
  @apply flex justify-between items-center h-8 px-2 py-[6px] hover:bg-secondary-50 rounded-[0.625rem] cursor-pointer border border-accent-200 rounded-[0.25rem];
}
.isActiveRow {
  @apply bg-secondary-100 border border-secondary-600 hover:border-accent-200;
}
.text {
  @apply text-gray-700 text-xs font-medium font-['Inter'] leading-none;
}
</style>

<style></style>
